// JQ4-42 · US-AUDIT-002 — TicketHistoryPage
//
// Admin page showing the chronological audit trail for a single ticket.
// GET /admin/tickets/:ticketId/history (mirrors TipConfigPage pattern)
//
// Access: org_admin and supervisor only (backend enforces 403 for operator).

import { getTicketHistory } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { AuditLogEntryResponse } from '@jq4race/shared';
import { Card, CardContent } from '@jq4race/ui';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { ACTION_LABELS, useActionLabel, useResolveActor } from '../audit-action-labels';

// Re-exported for backward compatibility — TicketHistoryPage.test.tsx imports
// ACTION_LABELS directly from this module. The canonical definition (incl.
// 'ticket.reopened', JQ4-28) now lives in '../audit-action-labels' and is
// shared with TicketDetailPage's audit-timeline section
// (admin-analytics-expansion D7, P3b review-fix — kills a page-to-page import).
export { ACTION_LABELS };

// ── Component ─────────────────────────────────────────────────────────────────

export function TicketHistoryPage() {
  const { t } = useTranslation('ticket-history');
  const resolveActionLabel = useActionLabel();
  const resolveActor = useResolveActor();
  const { ticketId } = useParams({ strict: false });

  const [items, setItems] = useState<AuditLogEntryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setErrorBanner(null);
    try {
      const response = await getTicketHistory(ticketId);
      setItems(response.items);
    } catch (e) {
      setErrorBanner(translateApiError(e, t, { fallback: t('errors.loadFailed') }));
    } finally {
      setLoading(false);
    }
  }, [ticketId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-[13px] text-jq-graphite">{t('common:states.loading')}</p>
          ) : items.length === 0 ? (
            <p className="text-[13px] text-jq-graphite/60">{t('empty')}</p>
          ) : (
            <table className="w-full text-[13px] text-jq-graphite">
              <thead>
                <tr className="border-b border-jq-sand/50 text-left text-[12px] text-jq-graphite/60">
                  <th className="pb-2 pr-4 font-medium">{t('table.action')}</th>
                  <th className="pb-2 pr-4 font-medium">{t('table.actor')}</th>
                  <th className="pb-2 font-medium">{t('table.time')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-jq-sand/30 last:border-0">
                    <td className="py-2 pr-4">{resolveActionLabel(item.action)}</td>
                    <td className="py-2 pr-4">{resolveActor(item)}</td>
                    <td className="py-2 font-mono text-[12px]">{item.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Feedback banner (mirrors TipConfigPage inline pattern) ─────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('ticket-history');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('errors.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
